/**
 * ArchiveModeTab — freeze/archive mode controls (pause API refresh + SGP generation).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, View, Text, StyleSheet, ScrollView, Platform, Switch } from 'react-native';
import { db } from '../firebaseConfig';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { showToast } from '../utils/toast';
import { adminStyles as styles } from './adminStyles';

export default function ArchiveModeTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [captureSaving, setCaptureSaving] = useState(false);
  const [snapshotStats, setSnapshotStats] = useState(null);

  const cfgRef = doc(db, 'app_config', 'archive_mode');

  useEffect(() => {
    const unsub = onSnapshot(cfgRef, (snap) => {
      setCfg(snap.exists() ? snap.data() : {});
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  // Also watch the latest snapshot manifest written by snapshotFirestore.js
  useEffect(() => {
    const statsRef = doc(db, 'app_config', 'archive_snapshot_stats');
    const unsub = onSnapshot(statsRef, (snap) => {
      if (snap.exists()) setSnapshotStats(snap.data());
    });
    return () => unsub();
  }, []);

  const freezeMode    = !!cfg?.freeze_mode;
  const captureEnabled = cfg?.capture_enabled !== false;
  const freezeTime    = cfg?.freeze_time || null;
  const updatedBy     = cfg?.updated_by || null;
  const updatedAt     = cfg?.updated_at?.toDate ? cfg.updated_at.toDate() : cfg?.updated_at ? new Date(cfg.updated_at) : null;

  const toggleFreezeMode = async () => {
    setSaving(true);
    try {
      const next = !freezeMode;
      const payload = {
        freeze_mode:     next,
        freeze_time:     next ? new Date().toISOString() : null,
        updated_at:      serverTimestamp(),
        updated_by:      'admin',
      };
      await setDoc(cfgRef, payload, { merge: true });
      showToast(
        next
          ? `Archive mode ON — API refreshes paused. Serving data frozen as of ${new Date().toLocaleString()}`
          : 'Archive mode OFF — live API refreshes will resume within 60 seconds',
        next ? 'info' : 'success'
      );
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleCapture = async () => {
    setCaptureSaving(true);
    try {
      const next = !captureEnabled;
      await setDoc(cfgRef, { capture_enabled: next, updated_at: serverTimestamp(), updated_by: 'admin' }, { merge: true });
      showToast(
        next
          ? 'GCS capture ON — odds/props/SGP writes are being archived to Firebase Storage'
          : 'GCS capture OFF — archive taps disabled (saves minor GCS write costs)',
        'success'
      );
    } catch (e) {
      showToast('Save failed: ' + (e?.message || String(e)), 'error');
    } finally {
      setCaptureSaving(false);
    }
  };

  const statusColor   = freezeMode ? '#F59E0B' : '#22C55E';
  const statusLabel   = freezeMode ? 'FROZEN' : 'LIVE';
  const statusBg      = freezeMode ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.10)';

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
      <Text style={styles.sectionTitle}>Archive Mode</Text>

      {/* ── Hero status card ─────────────────────────────────────────── */}
      <View style={[amStyles.heroCard, { borderColor: statusColor, backgroundColor: statusBg }]}>
        <View style={amStyles.heroRow}>
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <View style={[amStyles.statusDot, { backgroundColor: statusColor }]} />
              <Text style={[amStyles.statusBadge, { color: statusColor }]}>{statusLabel}</Text>
            </View>
            <Text style={amStyles.heroTitle}>
              {freezeMode ? 'App is in Archive Mode' : 'App is in Live Mode'}
            </Text>
            <Text style={amStyles.heroDesc}>
              {freezeMode
                ? `Serving frozen data from ${freezeTime ? new Date(freezeTime).toLocaleString() : 'unknown'}. All API refreshes are paused.`
                : 'Live OpticOdds API is active. All scheduled refreshes running normally.'}
            </Text>
            {updatedAt && (
              <Text style={amStyles.heroMeta}>
                Last changed {updatedAt.toLocaleString()}{updatedBy ? ` by ${updatedBy}` : ''}
              </Text>
            )}
          </View>
          <View style={{ alignItems: 'center', gap: 8 }}>
            {loading || saving ? (
              <ActivityIndicator color={statusColor} />
            ) : (
              <Switch
                value={freezeMode}
                onValueChange={toggleFreezeMode}
                trackColor={{ false: 'rgba(255,255,255,0.15)', true: '#F59E0B' }}
                thumbColor={freezeMode ? '#fff' : 'rgba(255,255,255,0.8)'}
              />
            )}
            <Text style={[amStyles.toggleLabel, { color: statusColor }]}>
              {freezeMode ? 'ON' : 'OFF'}
            </Text>
          </View>
        </View>
      </View>

      {/* ── What happens when toggled ─────────────────────────────────── */}
      <View style={amStyles.section}>
        <Text style={amStyles.sectionLabel}>What the toggle does</Text>
        <View style={amStyles.bulletList}>
          {[
            { on: 'All 30+ scheduled Cloud Functions skip API calls within 60s', off: 'All scheduled Cloud Functions resume API calls within 60s' },
            { on: 'SGP queries use frozen timestamp as "now" — no data gets filtered out as past/stale', off: 'SGP queries use real-time — live game filters and freshness windows apply normally' },
            { on: 'Archived Firestore data serves all queries (no new writes, near-zero cost)', off: 'Full live pipeline: odds, props, SGPs refreshed every 15–60 min' },
            { on: 'GCS capture taps continue writing to Firebase Storage (line movement preserved)', off: 'GCS capture taps continue writing to Firebase Storage' },
          ].map((item, i) => (
            <View key={i} style={amStyles.bulletRow}>
              <Ionicons
                name={freezeMode ? 'checkmark-circle' : 'ellipse-outline'}
                size={14}
                color={freezeMode ? '#F59E0B' : 'rgba(255,255,255,0.4)'}
                style={{ marginTop: 2 }}
              />
              <Text style={amStyles.bulletText}>{freezeMode ? item.on : item.off}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* ── GCS capture toggle ────────────────────────────────────────── */}
      <View style={amStyles.card}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flex: 1, marginRight: 16 }}>
            <Text style={amStyles.cardTitle}>GCS Archive Capture</Text>
            <Text style={amStyles.cardDesc}>
              Fire-and-forget taps on every odds/props/SGP write — archived to Firebase Storage.
              {captureEnabled ? ' Currently writing.' : ' Currently paused.'}
            </Text>
          </View>
          {captureSaving ? (
            <ActivityIndicator color="#9fd3ff" />
          ) : (
            <Switch
              value={captureEnabled}
              onValueChange={toggleCapture}
              trackColor={{ false: 'rgba(255,255,255,0.15)', true: '#22C55E' }}
              thumbColor={captureEnabled ? '#fff' : 'rgba(255,255,255,0.8)'}
              disabled={loading}
            />
          )}
        </View>
      </View>

      {/* ── Archive health ────────────────────────────────────────────── */}
      <View style={amStyles.section}>
        <Text style={amStyles.sectionLabel}>Archive Health</Text>
        <View style={amStyles.statGrid}>
          {[
            { label: 'Snapshot Slots', value: snapshotStats?.total_slots ?? '—', icon: 'time-outline', color: '#9fd3ff' },
            { label: 'Newest Slot', value: snapshotStats?.newest_slot ?? '—', icon: 'cloud-upload-outline', color: '#22C55E' },
            { label: 'SGP Fixtures', value: snapshotStats?.sgp_cache_archived ?? '—', icon: 'layers-outline', color: '#A855F7' },
            { label: 'GCS Parlay Responses', value: snapshotStats?.parlay_responses ?? '—', icon: 'analytics-outline', color: '#F59E0B' },
          ].map((s) => (
            <View key={s.label} style={amStyles.statCard}>
              <Ionicons name={s.icon} size={18} color={s.color} />
              <Text style={[amStyles.statValue, { color: s.color }]}>{String(s.value)}</Text>
              <Text style={amStyles.statLabel}>{s.label}</Text>
            </View>
          ))}
        </View>
        <Text style={amStyles.hintText}>
          Run{' '}
          <Text style={amStyles.code}>node scripts/verifyArchive.js</Text>
          {' '}anytime to get a full health report. Stats above update when the local snapshot script runs.
        </Text>
      </View>

      {/* ── Day-7 cutover checklist ───────────────────────────────────── */}
      <View style={amStyles.section}>
        <Text style={amStyles.sectionLabel}>API Cutoff Checklist</Text>
        {[
          { step: '1', label: 'Run final baseline export', detail: 'node scripts/exportFirestore.js  (the day before cutoff)' },
          { step: '2', label: 'Run archive verify', detail: 'node scripts/verifyArchive.js  (confirm everything looks healthy)' },
          { step: '3', label: 'Pull GCS archive locally', detail: 'gsutil -m rsync -r gs://bet-optimized-fe922.firebasestorage.app/archive ./data-archive' },
          { step: '4', label: 'Flip the toggle above', detail: 'Toggle ON — takes effect across all functions within 60 seconds. No redeploy.' },
          { step: '5', label: 'Verify zero results regression', detail: 'Run a few queries (nba sgps, mlb dk sgps) and confirm results still appear' },
          { step: '6', label: 'Revoke OpticOdds API key', detail: 'Do this last, after confirming archive mode is working' },
        ].map((item) => (
          <View key={item.step} style={amStyles.checkRow}>
            <View style={amStyles.checkBadge}>
              <Text style={amStyles.checkBadgeText}>{item.step}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={amStyles.checkLabel}>{item.label}</Text>
              <Text style={amStyles.checkDetail}>{item.detail}</Text>
            </View>
          </View>
        ))}
      </View>

      {/* ── Scheduled tasks status ────────────────────────────────────── */}
      <View style={amStyles.section}>
        <Text style={amStyles.sectionLabel}>Scheduled Tasks</Text>
        {[
          { label: 'Odds refresh', schedule: 'Every 15 min', status: freezeMode ? 'skipping' : 'running' },
          { label: 'Player props refresh', schedule: 'Every hour (:07–:17)', status: freezeMode ? 'skipping' : 'running' },
          { label: 'SGP automation', schedule: 'Every 30 min', status: freezeMode ? 'skipping' : 'running' },
          { label: 'Best bets cache', schedule: 'Every 30 min', status: freezeMode ? 'skipping' : 'running' },
          { label: 'GCS archive capture', schedule: 'On every write', status: captureEnabled ? 'active' : 'paused' },
          { label: 'Local snapshot (LaunchAgent)', schedule: 'Every 60 min (laptop awake)', status: 'installed' },
        ].map((t) => (
          <View key={t.label} style={amStyles.scheduleRow}>
            <View style={{ flex: 1 }}>
              <Text style={amStyles.scheduleLabel}>{t.label}</Text>
              <Text style={amStyles.scheduleTime}>{t.schedule}</Text>
            </View>
            <View style={[amStyles.scheduleBadge, {
              backgroundColor:
                t.status === 'running' || t.status === 'active' || t.status === 'installed'
                  ? 'rgba(34,197,94,0.15)'
                  : t.status === 'skipping'
                  ? 'rgba(245,158,11,0.15)'
                  : 'rgba(255,255,255,0.08)',
            }]}>
              <Text style={[amStyles.scheduleBadgeText, {
                color:
                  t.status === 'running' || t.status === 'active' || t.status === 'installed'
                    ? '#22C55E'
                    : t.status === 'skipping'
                    ? '#F59E0B'
                    : 'rgba(255,255,255,0.5)',
              }]}>{t.status}</Text>
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

const amStyles = StyleSheet.create({
  heroCard:    { borderRadius: 14, borderWidth: 1.5, padding: 18, marginBottom: 20 },
  heroRow:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  statusDot:   { width: 8, height: 8, borderRadius: 4 },
  statusBadge: { fontSize: 11, fontWeight: '800', letterSpacing: 1.2 },
  heroTitle:   { color: '#fff', fontSize: 17, fontWeight: '700', marginBottom: 6 },
  heroDesc:    { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 19, maxWidth: 320 },
  heroMeta:    { color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 8 },
  toggleLabel: { fontSize: 11, fontWeight: '700', letterSpacing: 0.5 },
  section:     { marginBottom: 20 },
  sectionLabel:{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 },
  card:        { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', padding: 14, marginBottom: 12 },
  cardTitle:   { color: '#fff', fontWeight: '600', marginBottom: 4, fontSize: 14 },
  cardDesc:    { color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 17 },
  bulletList:  { gap: 8 },
  bulletRow:   { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
  bulletText:  { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 18, flex: 1 },
  statGrid:    { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 10 },
  statCard:    { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', padding: 12, alignItems: 'center', gap: 4, minWidth: 120, flex: 1 },
  statValue:   { fontSize: 18, fontWeight: '700' },
  statLabel:   { color: 'rgba(255,255,255,0.5)', fontSize: 11, textAlign: 'center' },
  hintText:    { color: 'rgba(255,255,255,0.45)', fontSize: 12, lineHeight: 17 },
  code:        { fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier', color: '#9fd3ff', fontSize: 11 },
  checkRow:    { flexDirection: 'row', gap: 12, alignItems: 'flex-start', marginBottom: 12 },
  checkBadge:  { width: 24, height: 24, borderRadius: 12, backgroundColor: 'rgba(159,211,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  checkBadgeText: { color: '#9fd3ff', fontSize: 12, fontWeight: '700' },
  checkLabel:  { color: '#fff', fontSize: 13, fontWeight: '600', marginBottom: 2 },
  checkDetail: { color: 'rgba(255,255,255,0.45)', fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
  scheduleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)' },
  scheduleLabel: { color: '#fff', fontSize: 13, fontWeight: '500' },
  scheduleTime: { color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 1 },
  scheduleBadge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 20 },
  scheduleBadgeText: { fontSize: 11, fontWeight: '600' },
});

